import * as React from 'react';
import { CollapsibleCard, cn } from '@insolech/ui';
import type {
  OffloadingsState,
  OffloadingThickness,
  MetatarsalHeads,
  MetatarsalCutOutLocation,
  MetatarsalCutOutSoftness,
  MetatarsalCutOutThickness,
  KineticWedgeThickness,
  ForefootHardness,
  SubcapitalBarMaterial,
  DropMetPadLocation,
  DropMetPadThickness,
  MttBarLocation,
  ExtraHeelCushionMaterial,
} from './prescriptionState';
import { ModCard, SideRow, SelectSm, SectionObservation } from './shared';
import { CARD_IDS } from './cardIds';

/**
 * OffloadingsSection (R31 rebuild).
 *
 * Card layout:
 *   Row A (full width): Metatarsal cut-outs (Horseshoe offloadings)
 *   Row B (50/50): Kinetic wedge          | Subcapital bar (MTT 1 to 5)
 *   Row C (50/50): Drop met pad (met dome)| MTT bar
 *   Row D (50/50): Metatarsal head cut-outs| Extra heel cushion pad
 *
 * Several cards gate the Thickness dropdown behind a primary selection
 * (Location / Select) so the user picks where first, then dials in
 * thickness - matching Rafael's reference image where Thickness shows as
 * greyed out until the primary is filled.
 *
 * Clinical option labels for Softness / Location strings are placeholders.
 * Refining them is a one-liner in the OPTIONS constants below.
 */

interface Props {
  value: OffloadingsState;
  onChange: (next: OffloadingsState) => void;
  /** Sandal-only branch (R37). When true, hides Metatarsal cut-outs
   *  (Horseshoe) and Kinetic wedge, reorders the remaining cards so
   *  Row 1 = Metatarsal head cut-outs | Subcapital bar and Row 2 =
   *  Toe pulp offloading (new sandal-only card) | Extra heel cushion. */
  isSandal?: boolean | undefined;
  readOnly?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  open?: boolean | undefined;
  onOpenChange?: ((next: boolean) => void) | undefined;
  /** Flip when the wizard wants required-but-empty dependent dropdowns
   *  (Softness/Thickness after a non-None Location pick) to paint red. */
  showErrors?: boolean | undefined;
}

const THICKNESS_OPTIONS: ReadonlyArray<{ value: OffloadingThickness; label: string }> = [
  { value: '1mm', label: '1mm' },
  { value: '2mm', label: '2mm' },
  { value: '3mm', label: '3mm' },
];

/** Metatarsal cut-outs (Horseshoe offloadings) - card-specific option lists. */
export const MCO_LOCATION_OPTIONS: ReadonlyArray<{
  value: MetatarsalCutOutLocation;
  label: string;
}> = [
  { value: 'none', label: 'None' },
  { value: 'cutout_m1', label: 'Cutout at M1' },
  { value: 'cutout_m2', label: 'Cutout at M2' },
  { value: 'cutout_m23', label: 'Cutout at M23' },
  { value: 'cutout_m234', label: 'Cutout at M234' },
  { value: 'cutout_m4', label: 'Cutout at M4' },
  { value: 'cutout_m5', label: 'Cutout at M5' },
];

export const MCO_SOFTNESS_OPTIONS: ReadonlyArray<{
  value: MetatarsalCutOutSoftness;
  label: string;
}> = [
  // Softest first. Carbosan is latex (a distinct material from the EVA
  // pair); the value encodes material + shore, so downstream labels derive
  // both from the enum.
  { value: 'carbosan_15_sa', label: 'Very soft (Carbosan 15 Shore A)' },
  { value: 'eva_35_sa', label: 'Light-soft (EVA 35 Shore A)' },
  { value: 'eva_65_sa', label: 'Medium-soft (EVA 65 Shore A)' },
];

export const MCO_THICKNESS_OPTIONS: ReadonlyArray<{
  value: MetatarsalCutOutThickness;
  label: string;
}> = [
  { value: '2mm', label: '2 mm' },
  { value: '3mm', label: '3 mm' },
];

/** Subcapital bar (MTT 1 to 5) - material pick; thickness is always 3mm
 *  and auto-set when the user picks a material. */
export const SUBCAPITAL_OPTIONS: ReadonlyArray<{
  value: SubcapitalBarMaterial;
  label: string;
}> = [
  { value: 'none', label: 'None' },
  { value: 'carbosan', label: 'Carbosan' },
  { value: 'poron', label: 'Poron' },
];

/** Kinetic wedge - Thickness options (2mm / 3mm). */
export const KINETIC_THICKNESS_OPTIONS: ReadonlyArray<{
  value: KineticWedgeThickness;
  label: string;
}> = [
  { value: '2mm', label: '2mm' },
  { value: '3mm', label: '3mm' },
];

/** Kinetic wedge - Softness options (None + Shore A grades). 'none' maps
 *  back to undefined on the schema; the row gates thickness off when
 *  softness isn't a real Shore A pick. */
export const KINETIC_SOFTNESS_OPTIONS: ReadonlyArray<{
  value: 'none' | ForefootHardness;
  label: string;
}> = [
  { value: 'none', label: 'None' },
  { value: 'shore_a_35', label: '35° Shore A' },
  { value: 'shore_a_65', label: '65° Shore A' },
];

/** Drop met pad (met dome) card-specific options. */
export const DROP_MET_PAD_LOCATION_OPTIONS: ReadonlyArray<{
  value: DropMetPadLocation;
  label: string;
}> = [
  { value: 'none', label: 'None' },
  { value: 'm2_3', label: '2 to 3' },
  { value: 'm3_4', label: '3 to 4' },
  { value: 'm2_4', label: '2 to 4' },
];

export const DROP_MET_PAD_THICKNESS_OPTIONS: ReadonlyArray<{
  value: DropMetPadThickness;
  label: string;
}> = [
  { value: '2mm', label: '2mm' },
  { value: '3mm', label: '3mm' },
  { value: '4mm', label: '4mm' },
  { value: '5mm', label: '5mm' },
  { value: '6mm', label: '6mm' },
];

/** MTT bar card-specific options. */
export const MTT_BAR_LOCATION_OPTIONS: ReadonlyArray<{
  value: MttBarLocation;
  label: string;
}> = [
  { value: 'none', label: 'None' },
  { value: 'm1_5', label: '1 to 5' },
  { value: 'm2_5', label: '2 to 5' },
  { value: 'm2_4', label: '2 to 4' },
];

/** Extra heel cushion pad - material options. Thickness auto-set to 3mm. */
export const EXTRA_HEEL_CUSHION_OPTIONS: ReadonlyArray<{
  value: ExtraHeelCushionMaterial;
  label: string;
}> = [
  { value: 'none', label: 'None' },
  { value: 'carbosan_standard', label: 'Carbosan (standard)' },
  { value: 'poron', label: 'Poron' },
];

const M_KEYS: ReadonlyArray<keyof MetatarsalHeads> = ['m1', 'm2', 'm3', 'm4', 'm5'];

export function OffloadingsSection({
  value,
  onChange,
  isSandal = false,
  readOnly,
  defaultOpen = false,
  open,
  onOpenChange,
  showErrors,
}: Props) {
  const set = <K extends keyof OffloadingsState>(k: K, v: OffloadingsState[K]) =>
    onChange({ ...value, [k]: v });

  const mco = value.metatarsal_cut_outs ?? {};
  const kw = value.kinetic_wedge ?? {};
  const sb = value.subcapital_bar ?? {};
  const dmp = value.drop_met_pad ?? {};
  const mtt = value.mtt_bar ?? {};
  const mhco = value.metatarsal_head_cut_outs ?? {};
  const ehcp = value.extra_heel_cushion_pad ?? {};
  const tpo = value.toe_pulp_offloading_heads ?? {};

  // Sandal-only first-mount defaults. Mirrors the Rearfoot subcalcaneal
  // pattern: seed Subcapital bar L+R = Poron (3mm) so new sandal orders
  // open with the clinically expected default. Insole flows are
  // unaffected (the gate is isSandal). Each side seeds independently so
  // an existing draft that already set one side keeps its value.
  const sandalInitialized = React.useRef(false);
  React.useEffect(() => {
    if (!isSandal) return;
    if (sandalInitialized.current) return;
    sandalInitialized.current = true;
    const next = { ...sb };
    let changed = false;
    if (next.left == null) {
      next.left = 'poron';
      next.left_thickness = '3mm';
      changed = true;
    }
    if (next.right == null) {
      next.right = 'poron';
      next.right_thickness = '3mm';
      changed = true;
    }
    if (changed) {
      onChange({ ...value, subcapital_bar: next });
    }
    // Intentionally only on first sandal mount; subsequent edits run
    // through the normal onChange path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSandal]);

  const isControlled = open !== undefined && onOpenChange !== undefined;
  return (
    <CollapsibleCard
      title="Offloadings"
      subtitle="Cut-outs, kinetic wedge, bars, pads, head cut-outs, cushion"
      {...(isControlled
        ? { open: open!, onOpenChange: onOpenChange! }
        : { defaultOpen })}
    >
      <div className="space-y-3">
        {/* R37 - Sandal layout takes over the whole section:
            Row 1: Metatarsal head cut-outs | Subcapital bar
            Row 2: Drop met pad (met dome)  | MTT bar
            Row 3: Toe pulp offloading (new)| Extra heel cushion pad
            Insole layout (below the else branch) stays identical to today. */}
        {isSandal ? (
          <>
            {/* Sandal Row 1: Metatarsal head cut-outs (L) + Subcapital bar (R) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ModCard
                id={CARD_IDS.metatarsalHeadCutOuts}
                title="Metatarsal head cut-outs"
                subtitle="Localised cut-outs under individual MTHs."
                image={{
                  src: '/prescription/modifications/metatarsal-head-cut-outs.png',
                  alt: 'Metatarsal head cut-outs reference',
                }}
              >
                <MetatarsalHeadsRow
                  side="L"
                  data={mhco.left ?? {}}
                  onChange={(v) =>
                    set('metatarsal_head_cut_outs', { ...mhco, left: v })
                  }
                  readOnly={readOnly}
                />
                <MetatarsalHeadsRow
                  side="R"
                  data={mhco.right ?? {}}
                  onChange={(v) =>
                    set('metatarsal_head_cut_outs', { ...mhco, right: v })
                  }
                  readOnly={readOnly}
                />
              </ModCard>

              <ModCard
                id={CARD_IDS.subcapitalBar}
                title="Subcapital bar (MTT 1 to 5)"
                subtitle="Support just proximal to the metatarsal heads."
                image={{
                  src: '/prescription/modifications/subcapital-bar.png',
                  alt: 'Subcapital bar reference',
                }}
              >
                <SubcapitalBarRow
                  side="L"
                  material={sb.left}
                  onChange={(material) =>
                    set('subcapital_bar', {
                      ...sb,
                      left: material,
                      left_thickness: material && material !== 'none' ? '3mm' : undefined,
                    })
                  }
                  readOnly={readOnly}
                />
                <SubcapitalBarRow
                  side="R"
                  material={sb.right}
                  onChange={(material) =>
                    set('subcapital_bar', {
                      ...sb,
                      right: material,
                      right_thickness: material && material !== 'none' ? '3mm' : undefined,
                    })
                  }
                  readOnly={readOnly}
                />
              </ModCard>
            </div>

            {/* Sandal Row 2: Drop met pad (L) + MTT bar (R). Same cards
                as the insole layout below; reproduced here to satisfy
                the sandal-specific row order Rafael asked for. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <SelectThicknessCard<DropMetPadThickness>
                id={CARD_IDS.dropMetPad}
                title="Drop met pad (met dome)"
                subtitle="Reduces pressure under a chosen metatarsal."
                imageSrc="/prescription/modifications/drop-met-pad.png"
                imageAlt="Drop met pad reference"
                primary={{
                  left: dmp.left_location,
                  right: dmp.right_location,
                  options: DROP_MET_PAD_LOCATION_OPTIONS,
                  placeholder: 'Location',
                }}
                thickness={{
                  left: dmp.left_thickness,
                  right: dmp.right_thickness,
                  options: DROP_MET_PAD_THICKNESS_OPTIONS,
                }}
                onChange={(patch) => {
                  const remap: Partial<typeof dmp> = {};
                  if ('left' in patch) {
                    remap.left_location = patch.left as DropMetPadLocation | undefined;
                    if (!patch.left || patch.left === 'none') {
                      remap.left_thickness = undefined;
                    } else if (!dmp.left_thickness) {
                      remap.left_thickness = '3mm';
                    }
                  }
                  if ('right' in patch) {
                    remap.right_location = patch.right as DropMetPadLocation | undefined;
                    if (!patch.right || patch.right === 'none') {
                      remap.right_thickness = undefined;
                    } else if (!dmp.right_thickness) {
                      remap.right_thickness = '3mm';
                    }
                  }
                  if ('left_thickness' in patch) remap.left_thickness = patch.left_thickness;
                  if ('right_thickness' in patch) remap.right_thickness = patch.right_thickness;
                  set('drop_met_pad', { ...dmp, ...remap });
                }}
                readOnly={readOnly}
                showErrors={showErrors}
              />

              <ModCard
                id={CARD_IDS.mttBar}
                title="MTT bar"
                subtitle="Metatarsal bar across the forefoot."
                image={{
                  src: '/prescription/modifications/mtt-bar.png',
                  alt: 'MTT bar reference',
                }}
              >
                <MttBarRow
                  side="L"
                  location={mtt.left_location}
                  onChange={(loc) =>
                    set('mtt_bar', {
                      ...mtt,
                      left_location: loc,
                      left_thickness: loc && loc !== 'none' ? '3mm' : undefined,
                    })
                  }
                  readOnly={readOnly}
                />
                <MttBarRow
                  side="R"
                  location={mtt.right_location}
                  onChange={(loc) =>
                    set('mtt_bar', {
                      ...mtt,
                      right_location: loc,
                      right_thickness: loc && loc !== 'none' ? '3mm' : undefined,
                    })
                  }
                  readOnly={readOnly}
                />
              </ModCard>
            </div>

            {/* Sandal Row 3: Toe pulp offloading (NEW, L) + Extra heel cushion pad (R) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* New sandal-only card. Same M1-M5 checkbox shape as
                  Metatarsal head cut-outs - reuses MetatarsalHeadsRow.
                  Image asset will be supplied by Rafael; if missing the
                  ModCard frame still renders cleanly inside its sand-50
                  placeholder box. */}
              <ModCard
                id={CARD_IDS.toePulpOffloading}
                title="Toe pulp offloading"
                subtitle="Localised offloading under the toe pulps."
                image={{
                  /* TODO Rafael: drop /prescription/modifications/toe-pulp-offloading.png */
                  src: '/prescription/modifications/toe-pulp-offloading.png',
                  alt: 'Toe pulp offloading reference',
                }}
              >
                <MetatarsalHeadsRow
                  side="L"
                  data={tpo.left ?? {}}
                  onChange={(v) =>
                    set('toe_pulp_offloading_heads', { ...tpo, left: v })
                  }
                  readOnly={readOnly}
                />
                <MetatarsalHeadsRow
                  side="R"
                  data={tpo.right ?? {}}
                  onChange={(v) =>
                    set('toe_pulp_offloading_heads', { ...tpo, right: v })
                  }
                  readOnly={readOnly}
                />
              </ModCard>

              <ModCard
                id={CARD_IDS.extraHeelCushionPad}
                title="Extra heel cushion pad"
                subtitle="Additional heel cushioning."
                image={{
                  src: '/prescription/modifications/extra-heel-cushion-pad.png',
                  alt: 'Extra heel cushion pad reference',
                }}
              >
                <ExtraHeelCushionRow
                  side="L"
                  material={ehcp.left}
                  onChange={(material) =>
                    set('extra_heel_cushion_pad', {
                      ...ehcp,
                      left: material,
                      left_thickness: material && material !== 'none' ? '3mm' : undefined,
                    })
                  }
                  readOnly={readOnly}
                />
                <ExtraHeelCushionRow
                  side="R"
                  material={ehcp.right}
                  onChange={(material) =>
                    set('extra_heel_cushion_pad', {
                      ...ehcp,
                      right: material,
                      right_thickness: material && material !== 'none' ? '3mm' : undefined,
                    })
                  }
                  readOnly={readOnly}
                />
              </ModCard>
            </div>
          </>
        ) : (
          <>
        {/* Row A: Metatarsal cut-outs (Horseshoe offloadings) - full width */}
        <MetatarsalCutOutsCard
          value={mco}
          onChange={(v) => set('metatarsal_cut_outs', v)}
          readOnly={readOnly}
          showErrors={showErrors}
        />

        {/* Row B: Kinetic wedge + Subcapital bar */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ModCard
            id={CARD_IDS.kineticWedge}
            title="Kinetic wedge"
            subtitle="Encourages first MTPJ dorsiflexion at toe-off."
            image={{
              src: '/prescription/modifications/kinetic-wedge.png',
              alt: 'Kinetic wedge reference',
            }}
          >
            <KineticWedgeRow
              side="L"
              data={kw}
              patch={(v) => set('kinetic_wedge', v)}
              readOnly={readOnly}
              showErrors={showErrors}
            />
            <KineticWedgeRow
              side="R"
              data={kw}
              patch={(v) => set('kinetic_wedge', v)}
              readOnly={readOnly}
              showErrors={showErrors}
            />
          </ModCard>

          <ModCard
            id={CARD_IDS.subcapitalBar}
            title="Subcapital bar (MTT 1 to 5)"
            subtitle="Support just proximal to the metatarsal heads."
            image={{
              src: '/prescription/modifications/subcapital-bar.png',
              alt: 'Subcapital bar reference',
            }}
          >
            <SubcapitalBarRow
              side="L"
              material={sb.left}
              onChange={(material) =>
                set('subcapital_bar', {
                  ...sb,
                  left: material,
                  // Auto-set / clear thickness based on whether a real
                  // material was picked - 3mm is the only thickness
                  // anyone ever orders for this card.
                  left_thickness: material && material !== 'none' ? '3mm' : undefined,
                })
              }
              readOnly={readOnly}
            />
            <SubcapitalBarRow
              side="R"
              material={sb.right}
              onChange={(material) =>
                set('subcapital_bar', {
                  ...sb,
                  right: material,
                  right_thickness: material && material !== 'none' ? '3mm' : undefined,
                })
              }
              readOnly={readOnly}
            />
          </ModCard>
        </div>

        {/* Row C: Drop met pad + MTT bar */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SelectThicknessCard<DropMetPadThickness>
            id={CARD_IDS.dropMetPad}
            title="Drop met pad (met dome)"
            subtitle="Reduces pressure under a chosen metatarsal."
            imageSrc="/prescription/modifications/drop-met-pad.png"
            imageAlt="Drop met pad reference"
            primary={{
              left: dmp.left_location,
              right: dmp.right_location,
              options: DROP_MET_PAD_LOCATION_OPTIONS,
              placeholder: 'Location',
            }}
            thickness={{
              left: dmp.left_thickness,
              right: dmp.right_thickness,
              options: DROP_MET_PAD_THICKNESS_OPTIONS,
            }}
            onChange={(patch) => {
              const remap: Partial<typeof dmp> = {};
              if ('left' in patch) {
                remap.left_location = patch.left as DropMetPadLocation | undefined;
                if (!patch.left || patch.left === 'none') {
                  // Clearing the location clears the thickness so it can't
                  // linger as stale state on the row object.
                  remap.left_thickness = undefined;
                } else if (!dmp.left_thickness) {
                  // R34 - auto-seed 3mm so the user sees the default thickness
                  // the instant they pick a real location.
                  remap.left_thickness = '3mm';
                }
              }
              if ('right' in patch) {
                remap.right_location = patch.right as DropMetPadLocation | undefined;
                if (!patch.right || patch.right === 'none') {
                  remap.right_thickness = undefined;
                } else if (!dmp.right_thickness) {
                  remap.right_thickness = '3mm';
                }
              }
              if ('left_thickness' in patch) remap.left_thickness = patch.left_thickness;
              if ('right_thickness' in patch) remap.right_thickness = patch.right_thickness;
              set('drop_met_pad', { ...dmp, ...remap });
            }}
            readOnly={readOnly}
            showErrors={showErrors}
          />

          {/* R34 - MTT bar now behaves like Subcapital bar / Extra heel
              cushion: thickness is always 3mm when a real location is
              picked. Picking the location alone is enough. */}
          <ModCard
            id={CARD_IDS.mttBar}
            title="MTT bar"
            subtitle="Metatarsal bar across the forefoot."
            image={{
              src: '/prescription/modifications/mtt-bar.png',
              alt: 'MTT bar reference',
            }}
          >
            <MttBarRow
              side="L"
              location={mtt.left_location}
              onChange={(loc) =>
                set('mtt_bar', {
                  ...mtt,
                  left_location: loc,
                  left_thickness: loc && loc !== 'none' ? '3mm' : undefined,
                })
              }
              readOnly={readOnly}
            />
            <MttBarRow
              side="R"
              location={mtt.right_location}
              onChange={(loc) =>
                set('mtt_bar', {
                  ...mtt,
                  right_location: loc,
                  right_thickness: loc && loc !== 'none' ? '3mm' : undefined,
                })
              }
              readOnly={readOnly}
            />
          </ModCard>
        </div>

        {/* Row D: Metatarsal head cut-outs + Extra heel cushion pad */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ModCard
            id={CARD_IDS.metatarsalHeadCutOuts}
            title="Metatarsal head cut-outs"
            subtitle="Localised cut-outs under individual MTHs."
            image={{
              src: '/prescription/modifications/metatarsal-head-cut-outs.png',
              alt: 'Metatarsal head cut-outs reference',
            }}
          >
            <MetatarsalHeadsRow
              side="L"
              data={mhco.left ?? {}}
              onChange={(v) =>
                set('metatarsal_head_cut_outs', { ...mhco, left: v })
              }
              readOnly={readOnly}
            />
            <MetatarsalHeadsRow
              side="R"
              data={mhco.right ?? {}}
              onChange={(v) =>
                set('metatarsal_head_cut_outs', { ...mhco, right: v })
              }
              readOnly={readOnly}
            />
          </ModCard>

          <ModCard
            id={CARD_IDS.extraHeelCushionPad}
            title="Extra heel cushion pad"
            subtitle="Additional heel cushioning."
            image={{
              src: '/prescription/modifications/extra-heel-cushion-pad.png',
              alt: 'Extra heel cushion pad reference',
            }}
          >
            <ExtraHeelCushionRow
              side="L"
              material={ehcp.left}
              onChange={(material) =>
                set('extra_heel_cushion_pad', {
                  ...ehcp,
                  left: material,
                  left_thickness: material && material !== 'none' ? '3mm' : undefined,
                })
              }
              readOnly={readOnly}
            />
            <ExtraHeelCushionRow
              side="R"
              material={ehcp.right}
              onChange={(material) =>
                set('extra_heel_cushion_pad', {
                  ...ehcp,
                  right: material,
                  right_thickness: material && material !== 'none' ? '3mm' : undefined,
                })
              }
              readOnly={readOnly}
            />
          </ModCard>
        </div>
          </>
        )}

        <SectionObservation
          value={value.observations}
          onChange={(v) => onChange({ ...value, observations: v })}
          disabled={readOnly}
          placeholder="Section-specific notes for offloadings…"
        />
      </div>
    </CollapsibleCard>
  );
}

// ---- Metatarsal cut-outs (Horseshoe offloadings) -----------------------

function MetatarsalCutOutsCard({
  value,
  onChange,
  readOnly,
  showErrors,
}: {
  value: NonNullable<OffloadingsState['metatarsal_cut_outs']>;
  onChange: (v: NonNullable<OffloadingsState['metatarsal_cut_outs']>) => void;
  readOnly?: boolean | undefined;
  showErrors?: boolean | undefined;
}) {
  const update = (patch: Partial<typeof value>) => onChange({ ...value, ...patch });

  return (
    <ModCard
      id={CARD_IDS.metatarsalCutOuts}
      title="Metatarsal cut-outs (Horseshoe offloadings)"
      subtitle="Horseshoe cut-out around a painful metatarsal head."
      image={{
        src: '/prescription/modifications/metatarsal-cut-outs.png',
        alt: 'Metatarsal cut-outs reference',
      }}
    >
      <MetatarsalCutOutRow
        side="L"
        location={value.left_location}
        softness={value.left_softness}
        thickness={value.left_thickness}
        poronFill={value.left_poron_fill ?? false}
        onChange={(p) => {
          const remap: Partial<typeof value> = {};
          if ('location' in p) remap.left_location = p.location;
          if ('softness' in p) remap.left_softness = p.softness;
          if ('thickness' in p) remap.left_thickness = p.thickness;
          if ('poronFill' in p) remap.left_poron_fill = p.poronFill;
          update(remap);
        }}
        readOnly={readOnly}
        showErrors={showErrors}
      />
      <MetatarsalCutOutRow
        side="R"
        location={value.right_location}
        softness={value.right_softness}
        thickness={value.right_thickness}
        poronFill={value.right_poron_fill ?? false}
        onChange={(p) => {
          const remap: Partial<typeof value> = {};
          if ('location' in p) remap.right_location = p.location;
          if ('softness' in p) remap.right_softness = p.softness;
          if ('thickness' in p) remap.right_thickness = p.thickness;
          if ('poronFill' in p) remap.right_poron_fill = p.poronFill;
          update(remap);
        }}
        readOnly={readOnly}
        showErrors={showErrors}
      />
    </ModCard>
  );
}

function MetatarsalCutOutRow({
  side,
  location,
  softness,
  thickness,
  poronFill,
  onChange,
  readOnly,
  showErrors,
}: {
  side: 'L' | 'R';
  location: MetatarsalCutOutLocation | undefined;
  softness: MetatarsalCutOutSoftness | undefined;
  thickness: MetatarsalCutOutThickness | undefined;
  poronFill: boolean;
  onChange: (p: {
    location?: MetatarsalCutOutLocation | undefined;
    softness?: MetatarsalCutOutSoftness | undefined;
    thickness?: MetatarsalCutOutThickness | undefined;
    poronFill?: boolean;
  }) => void;
  readOnly?: boolean | undefined;
  showErrors?: boolean | undefined;
}) {
  // A real cut-out is anything past "None". When location is unset OR
  // "None", the dependent fields stay disabled per Rafael's spec image.
  const filled = Boolean(location) && location !== 'none';
  return (
    <SideRow side={side}>
      <SelectSm
        value={location}
        onChange={(v) => {
          // Switching to None clears the dependent softness / thickness /
          // poronFill so they can't survive as stale state on the row.
          if (!v || v === 'none') {
            onChange({
              location: v,
              softness: undefined,
              thickness: undefined,
              poronFill: false,
            });
          } else {
            // Seed clinical defaults (Light-soft EVA 35 Shore A, 2mm) when
            // the user picks a real cut-out location. Only seed each field
            // if currently empty - never overwrite an existing user pick.
            const patch: {
              location: MetatarsalCutOutLocation;
              softness?: MetatarsalCutOutSoftness;
              thickness?: MetatarsalCutOutThickness;
            } = { location: v };
            if (softness === undefined) patch.softness = 'eva_35_sa';
            if (thickness === undefined) patch.thickness = '2mm';
            onChange(patch);
          }
        }}
        options={MCO_LOCATION_OPTIONS}
        disabled={readOnly}
        placeholder="Location"
        className="w-40"
      />
      <SelectSm
        value={softness}
        onChange={(v) => onChange({ softness: v })}
        options={MCO_SOFTNESS_OPTIONS}
        disabled={readOnly || !filled}
        placeholder="Softness"
        className="w-56"
        invalid={!!(showErrors && filled && !softness)}
      />
      <SelectSm
        value={thickness}
        onChange={(v) => onChange({ thickness: v })}
        options={MCO_THICKNESS_OPTIONS}
        disabled={readOnly || !filled}
        placeholder="Thickness"
        className="w-24"
        invalid={!!(showErrors && filled && !thickness)}
      />
      <label
        className={cn(
          'inline-flex items-center gap-1.5 text-xs',
          readOnly || !filled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <input
          type="checkbox"
          checked={poronFill}
          onChange={(e) => onChange({ poronFill: e.target.checked })}
          disabled={readOnly || !filled}
          className="h-3.5 w-3.5 rounded border-border-default text-brand-600 focus:ring-brand-500 focus:ring-offset-0"
        />
        Poron fill
      </label>
    </SideRow>
  );
}

// ---- Kinetic wedge row ---------------------------------------------------

function KineticWedgeRow({
  side,
  data,
  patch,
  readOnly,
  showErrors,
}: {
  side: 'L' | 'R';
  data: NonNullable<OffloadingsState['kinetic_wedge']>;
  patch: (v: NonNullable<OffloadingsState['kinetic_wedge']>) => void;
  readOnly?: boolean | undefined;
  showErrors?: boolean | undefined;
}) {
  const isLeft = side === 'L';
  // R31 rename - `left_height` is now `left_thickness`. Read from either
  // so old drafts deserialize cleanly; new writes go to the new field.
  const thickness = isLeft
    ? data.left_thickness ?? data.left_height
    : data.right_thickness ?? data.right_height;
  const softness = isLeft ? data.left_softness : data.right_softness;
  // Thickness is gated until a real Shore A pick is in place - matches the
  // greyed-out pattern Rafael uses on Drop met pad and the others.
  const softnessFilled = Boolean(softness);
  return (
    <SideRow side={side}>
      <SelectSm<'none' | ForefootHardness>
        value={softness}
        onChange={(v) => {
          // 'none' maps back to undefined so the schema stays clean. Also
          // clears the dependent thickness when the user backs out.
          const nextSoftness = v === 'none' ? undefined : v;
          // Seed thickness to '2mm' on first softness pick so the user can
          // tab through without an extra click; existing thickness values
          // (including the legacy left_height/right_height fields) win.
          const seedThickness = nextSoftness && !thickness ? '2mm' : undefined;
          patch({
            ...data,
            ...(isLeft
              ? {
                  left_softness: nextSoftness,
                  ...(nextSoftness
                    ? seedThickness
                      ? { left_thickness: seedThickness }
                      : {}
                    : { left_thickness: undefined }),
                }
              : {
                  right_softness: nextSoftness,
                  ...(nextSoftness
                    ? seedThickness
                      ? { right_thickness: seedThickness }
                      : {}
                    : { right_thickness: undefined }),
                }),
          });
        }}
        options={KINETIC_SOFTNESS_OPTIONS}
        disabled={readOnly}
        placeholder="Softness"
        className="w-28"
      />
      <SelectSm
        value={thickness}
        onChange={(v) =>
          patch({ ...data, ...(isLeft ? { left_thickness: v } : { right_thickness: v }) })
        }
        options={KINETIC_THICKNESS_OPTIONS}
        disabled={readOnly || !softnessFilled}
        placeholder="Thickness"
        className="w-24"
        invalid={!!(showErrors && softnessFilled && !thickness)}
      />
    </SideRow>
  );
}

// ---- Extra heel cushion pad row (material only - thickness auto-set to 3mm) ----

function ExtraHeelCushionRow({
  side,
  material,
  onChange,
  readOnly,
}: {
  side: 'L' | 'R';
  material: ExtraHeelCushionMaterial | undefined;
  onChange: (v: ExtraHeelCushionMaterial | undefined) => void;
  readOnly?: boolean | undefined;
}) {
  const showThickness = material && material !== 'none';
  return (
    <SideRow side={side}>
      <SelectSm
        value={material}
        onChange={onChange}
        options={EXTRA_HEEL_CUSHION_OPTIONS}
        disabled={readOnly}
        placeholder="Select"
        className="flex-1 min-w-[8rem]"
      />
      {showThickness ? (
        <span className="inline-flex items-center h-7 px-2 rounded-md border border-border-subtle bg-sand-50 text-xs text-text-muted">
          3mm
        </span>
      ) : null}
    </SideRow>
  );
}

// ---- Subcapital bar row (material only - thickness auto-set to 3mm) ----

function SubcapitalBarRow({
  side,
  material,
  onChange,
  readOnly,
}: {
  side: 'L' | 'R';
  material: SubcapitalBarMaterial | undefined;
  onChange: (v: SubcapitalBarMaterial | undefined) => void;
  readOnly?: boolean | undefined;
}) {
  const showThickness = material && material !== 'none';
  return (
    <SideRow side={side}>
      <SelectSm
        value={material}
        onChange={onChange}
        options={SUBCAPITAL_OPTIONS}
        disabled={readOnly}
        placeholder="Select"
        className="flex-1 min-w-[8rem]"
      />
      {showThickness ? (
        <span className="inline-flex items-center h-7 px-2 rounded-md border border-border-subtle bg-sand-50 text-xs text-text-muted">
          3mm
        </span>
      ) : null}
    </SideRow>
  );
}

// ---- MTT bar row (R34: thickness is always 3mm, mirrors Subcapital bar) -

function MttBarRow({
  side,
  location,
  onChange,
  readOnly,
}: {
  side: 'L' | 'R';
  location: MttBarLocation | undefined;
  onChange: (v: MttBarLocation | undefined) => void;
  readOnly?: boolean | undefined;
}) {
  const showThickness = location && location !== 'none';
  return (
    <SideRow side={side}>
      <SelectSm
        value={location}
        onChange={onChange}
        options={MTT_BAR_LOCATION_OPTIONS}
        disabled={readOnly}
        placeholder="Location"
        className="flex-1 min-w-[8rem]"
      />
      {showThickness ? (
        <span className="inline-flex items-center h-7 px-2 rounded-md border border-border-subtle bg-sand-50 text-xs text-text-muted">
          3mm
        </span>
      ) : null}
    </SideRow>
  );
}

// ---- Generic "primary Select + Thickness" card --------------------------

interface SelectThicknessCardProps<T extends string = string> {
  title: string;
  subtitle: string;
  imageSrc: string;
  imageAlt: string;
  id?: string | undefined;
  primary: {
    left: string | undefined;
    right: string | undefined;
    options: ReadonlyArray<{ value: string; label: string }>;
    placeholder: string;
  };
  thickness: {
    left: T | undefined;
    right: T | undefined;
    /** Card-specific thickness options (defaults to 1mm/2mm/3mm). */
    options?: ReadonlyArray<{ value: T; label: string }>;
  };
  /** Patch keys: left / right / left_thickness / right_thickness. */
  onChange: (patch: {
    left?: string | undefined;
    right?: string | undefined;
    left_thickness?: T | undefined;
    right_thickness?: T | undefined;
  }) => void;
  readOnly?: boolean | undefined;
  showErrors?: boolean | undefined;
}

function SelectThicknessCard<T extends string = string>({
  title,
  subtitle,
  imageSrc,
  imageAlt,
  id,
  primary,
  thickness,
  onChange,
  readOnly,
  showErrors,
}: SelectThicknessCardProps<T>) {
  const isReal = (v: string | undefined) => Boolean(v) && v !== 'none';
  const thicknessOpts = (thickness.options ??
    THICKNESS_OPTIONS) as ReadonlyArray<{ value: T; label: string }>;
  return (
    <ModCard
      id={id}
      title={title}
      subtitle={subtitle}
      image={{ src: imageSrc, alt: imageAlt }}
    >
      <SideRow side="L">
        <SelectSm
          value={primary.left}
          onChange={(v) => onChange({ left: v })}
          options={primary.options}
          disabled={readOnly}
          placeholder={primary.placeholder}
          className="flex-1 min-w-[8rem]"
        />
        <SelectSm
          value={thickness.left}
          onChange={(v) => onChange({ left_thickness: v })}
          options={thicknessOpts}
          disabled={readOnly || !isReal(primary.left)}
          placeholder="Thickness"
          className="w-24"
          invalid={!!(showErrors && isReal(primary.left) && !thickness.left)}
        />
      </SideRow>
      <SideRow side="R">
        <SelectSm
          value={primary.right}
          onChange={(v) => onChange({ right: v })}
          options={primary.options}
          disabled={readOnly}
          placeholder={primary.placeholder}
          className="flex-1 min-w-[8rem]"
        />
        <SelectSm
          value={thickness.right}
          onChange={(v) => onChange({ right_thickness: v })}
          options={thicknessOpts}
          disabled={readOnly || !isReal(primary.right)}
          placeholder="Thickness"
          className="w-24"
          invalid={!!(showErrors && isReal(primary.right) && !thickness.right)}
        />
      </SideRow>
    </ModCard>
  );
}

// ---- Metatarsal head cut-outs (M1-M5 checkboxes per side) ---------------

function MetatarsalHeadsRow({
  side,
  data,
  onChange,
  readOnly,
}: {
  side: 'L' | 'R';
  data: MetatarsalHeads;
  onChange: (v: MetatarsalHeads) => void;
  readOnly?: boolean | undefined;
}) {
  return (
    <SideRow side={side}>
      {M_KEYS.map((key) => (
        <label
          key={key}
          className={cn(
            'inline-flex items-center gap-1 text-xs',
            readOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          <input
            type="checkbox"
            checked={data[key] ?? false}
            onChange={(e) => onChange({ ...data, [key]: e.target.checked })}
            disabled={readOnly}
            className="h-3.5 w-3.5 rounded border-border-default text-brand-600 focus:ring-brand-500 focus:ring-offset-0"
          />
          {key.toUpperCase()}
        </label>
      ))}
    </SideRow>
  );
}
